import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, IconButton, MenuItem, Popover, Typography } from '@mui/material';
import { styled } from '@mui/system';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import WorkIcon from '@mui/icons-material/Work';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { useNavigate } from 'react-router-dom';
import { ListItemIcon } from '@mui/material';
import logoUrl from '../Images/VaculinkIcon/vaculink-logo.png';

const expandedDrawerWidth = 60;

const AppBarStyled = styled(AppBar)({
    backgroundColor: 'white',
    height: '70px',
    borderBottom: '2px solid dodgerblue',
    width: '100%',
    transition: 'margin 0.8s',
    '&.closed': {
        marginLeft: 0,
    },
    '&.open': {
        marginLeft: expandedDrawerWidth - 50,
    },
});

const GradientText = styled('span')(({ theme }) => ({
    fontSize: '18px', // default font size for large screens
    fontFamily: 'Sans-serif',
    position: 'relative',
    display: 'inline-block',
    backgroundImage: 'linear-gradient(to right, #666, #666)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'white',
    textShadow: '1px 1px 1.5px rgba(0, 0, 0, 0.3)',
    textDecoration: 'none',

    // Adjust font size for medium screens
    [theme.breakpoints.down('md')]: {
        fontSize: '18px',
    },

    // Adjust font size for small screens
    [theme.breakpoints.down('sm')]: {
        fontSize: '14px',
    },

    // Adjust font size for extra-small screens
    [theme.breakpoints.down('xs')]: {
        fontSize: '12px',
    },
}));


const LocalTimeText = styled('div')({
    fontSize: '0.8rem',
    fontWeight: '500',
    color: '#666',
    padding: '3px 6px',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    display: 'inline-block',
    transition: 'background-color 0.3s ease, color 0.3s ease',
    textAlign: 'center',
    '&:hover': {
        backgroundColor: '#f5f5f5',
        color: '#333',
    },
    '@media (max-width: 600px)': {
        fontSize: '0.8rem',
        padding: '2px 4px',
    },
});


const LocalTimeLocation = styled('div')({
    fontSize: '0.9rem',
    fontWeight: '500',
    color: '#666',
    padding: '3px 6px',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    display: 'inline-block',
    transition: 'background-color 0.3s ease, color 0.3s ease',
    textAlign: 'center',
    '&:hover': {
        backgroundColor: '#f5f5f5',
        color: '#333',
    },
    '@media (max-width: 600px)': {
        fontSize: '0.8rem',
        padding: '2px 4px',
    },
});

const Logo = styled('img')({
    marginTop: '5px',
    width: '100px',
    marginLeft: '-10px',
});

const HeaderMenuButtonStyled = styled(IconButton)({
    '& .MuiSvgIcon-root': {
        color: 'black',
    },
});

const MoreVertIconBlack = styled(MenuIcon)({
    color: '#333',
    borderRadius: '50%',
    fontSize: '36px',
    padding: '6px',
    border: '2px solid dodgerblue',
    backgroundColor: '#F5F5F5',
    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.2)',
});


const Header = ({ loggedInUser, onLogout, username, userRole }) => {
    const navigate = useNavigate();
    const [isLoggedIn, setIsLoggedIn] = useState(/* initial login state */);
    const [anchorEl, setAnchorEl] = useState(null);
    const [localTime, setLocalTime] = useState('');
    const [location, setLocation] = useState('');

    useEffect(() => {
        const updateLocalTime = () => {
            const now = new Date();

            // Get the offset in minutes from loggedInUser.TimeZone
            const offsetMinutes = loggedInUser?.TimeDiff || 0;

            // Create a new Date object with the Netherlands time
            const options = {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZone: 'Europe/Amsterdam',
            };
            const amsterdamTime = new Intl.DateTimeFormat('en-GB', options).format(now);

            // Calculate the adjusted time by subtracting the offset
            const amsterdamDate = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Amsterdam" }));
            amsterdamDate.setMinutes(amsterdamDate.getMinutes() + offsetMinutes);

            // Format the adjusted time
            const adjustedTimeOptions = {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            };
            const adjustedTimeString = new Intl.DateTimeFormat('en-GB', adjustedTimeOptions).format(amsterdamDate);

            const [date, time] = adjustedTimeString.split(', ');
            setLocalTime({ date, time });
        };

        updateLocalTime();
        const interval = setInterval(updateLocalTime, 1000); // Update every second


        return () => clearInterval(interval);
    }, [loggedInUser?.TimeDiff]); // Re-run effect if TimeZone changes


    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleProfileAccountClick = () => {
        setAnchorEl(null);
        navigate('/user');
    };

    const handleLogout = () => {
        setIsLoggedIn(false);
        sessionStorage.removeItem('isLoggedIn');
        sessionStorage.removeItem('username');
        window.location.href = '/Login';
        handleMenuClose();
    };

    // Update the handleGoBack function
    const handleGoBack = () => {
        handleLogout(); // Call logout function
        navigate(-3); // Navigate back to the previous page
    };

    const roleMapping = {
        1: 'Super Admin',
        2: 'Admin_M',
        3: 'Admin',
        4: 'User',
    };

    return (
        <>
            <AppBarStyled>
                <Toolbar
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Logo src={logoUrl} alt="Logo" />
                    <LocalTimeText>
                        <div>{localTime.date}</div>
                        <div>{localTime.time}</div>

                    </LocalTimeText>
                    <Typography
                        variant="h7"
                        sx={{
                            fontFamily: 'var(--fa-font-brands)',
                            lineHeight: 'var(--bs-body-line-height)',
                            fontSize: '1.2rem',
                            position: 'relative',
                            flexGrow: 1, // Takes up available space
                            textAlign: 'center', // Center the text
                        }}
                    >
                        <GradientText>{loggedInUser && loggedInUser.project_description} -</GradientText>
                        <GradientText>
                            <div> - {loggedInUser.Location}</div>
                        </GradientText>
                    </Typography>
                    {/*
                    {loggedInUser?.IsMasterAdmin && (
                        <IconButton onClick={handleGoBack} title="Go back to Master Dashboard">
                            <ArrowBackIcon style={{ color: 'red' }} />
                        </IconButton>
                    )} */}
                    <HeaderMenuButtonStyled onClick={handleMenuClick}>
                        <MoreVertIconBlack />
                    </HeaderMenuButtonStyled>
                    <Popover
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        PaperProps={{
                            style: {
                                backgroundColor: '#333',
                            },
                        }}
                    >
                        <MenuItem className="hoveredMenuItem" style={{ color: 'white' }}>
                            <ListItemIcon style={{ color: 'white' }}>
                                <AccountCircleIcon fontSize="small" style={{ color: 'inherit' }} />
                            </ListItemIcon>
                            User: {loggedInUser && loggedInUser.username}
                        </MenuItem>
                        <MenuItem className="hoveredMenuItem" style={{ color: 'white' }}>
                            <ListItemIcon style={{ color: 'white' }}>
                                <WorkIcon fontSize="small" style={{ color: 'inherit' }} />
                            </ListItemIcon>
                            Role: {loggedInUser && loggedInUser.RoleID ? roleMapping[loggedInUser.RoleID] || 'NA' : 'NA'}
                        </MenuItem>

                        {/* 
                        <MenuItem className="hoveredMenuItem2" style={{ color: 'white' }} onClick={handleProfileAccountClick}>
                            <ListItemIcon style={{ color: 'white' }}>
                                <ManageAccountsIcon fontSize="small" style={{ color: 'inherit' }} />
                            </ListItemIcon>
                            Profile & Account
                        </MenuItem>
                        */}

                        <MenuItem className="hoveredMenuItem1" style={{ color: 'white' }} onClick={handleLogout}>
                            <ListItemIcon style={{ color: 'white' }}>
                                <ExitToAppIcon fontSize="small" style={{ color: 'inherit' }} />
                            </ListItemIcon>
                            Logout
                        </MenuItem>
                    </Popover>
                </Toolbar>
            </AppBarStyled>
        </>
    );
};

export default Header;